<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded" class="intelligence-analytics pa-4">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <h1 class="text-h5 font-weight-bold theme-heading">
              Platform Analytics
            </h1>
            <p class="text-subtitle-2 grey--text text--darken-1 mb-0">
              Intelligence platform usage and profile inventory metrics
            </p>
          </div>
          <v-btn-toggle v-model="selectedPeriodIdx" mandatory dense>
            <v-btn small :value="0">7d</v-btn>
            <v-btn small :value="1">14d</v-btn>
            <v-btn small :value="2">30d</v-btn>
          </v-btn-toggle>
        </div>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" class="mb-4" />

    <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
      <v-tab>Platform Overview</v-tab>
      <v-tab>Search Audit</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-row v-if="profileInventory">
          <v-col cols="12" sm="6" md="3" v-for="stat in inventoryStats" :key="stat.label">
            <v-card outlined class="pa-4 text-center">
              <div class="text-h4 font-weight-bold" :style="{ color: stat.color }">
                {{ stat.value }}
              </div>
              <div class="text-caption grey--text text--darken-1 mt-1">{{ stat.label }}</div>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="pa-4 mt-4 mb-4" v-if="profileInventory">
          <div class="d-flex align-center justify-space-between" style="cursor: pointer" @click="healthMetricsOpen = !healthMetricsOpen">
            <div class="d-flex align-center" style="gap: 8px">
              <v-icon small color="#007AFF">mdi-heart-pulse</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold theme-heading mb-0">Platform Health Metrics</h3>
            </div>
            <v-icon small>{{ healthMetricsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <div v-if="healthMetricsOpen" class="mt-4">
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-chip x-small outlined color="#007AFF">Static</v-chip>
              <span class="text-caption grey--text">Always true — independent of time period</span>
            </div>
            <v-row dense class="mb-4">
              <v-col cols="6" sm="4" md="2" v-for="m in staticMetrics" :key="m.label">
                <div class="health-metric-card pa-3 text-center">
                  <div class="text-h6 font-weight-bold" style="color: #007AFF">{{ m.value }}</div>
                  <div class="text-caption grey--text" style="line-height: 1.2">{{ m.label }}</div>
                </div>
              </v-col>
            </v-row>
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-chip x-small outlined color="#F59E0B">Period</v-chip>
              <span class="text-caption grey--text">Changes with {{ periodOptions[selectedPeriodIdx] }}-day selection</span>
            </div>
            <v-row dense>
              <v-col cols="6" sm="4" md="3" v-for="m in periodMetrics" :key="m.label">
                <div class="health-metric-card pa-3 text-center">
                  <div class="text-h6 font-weight-bold" style="color: #F59E0B">{{ m.value }}</div>
                  <div class="text-caption grey--text" style="line-height: 1.2">{{ m.label }}</div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card>

        <v-card outlined class="pa-4 mt-4 mb-4" v-if="searchesOverTime.length > 0">
          <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
            Searches Over Time
          </h3>
          <div class="chart-container mb-4">
            <canvas ref="searchesChart" height="220"></canvas>
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Date</th>
                <th>Search Count</th>
                <th>Avg Results</th>
                <th>Avg Speed (ms)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in searchesOverTime" :key="entry.date">
                <td class="text-body-2">{{ entry.date }}</td>
                <td class="text-body-2 font-weight-bold">{{ entry.count }}</td>
                <td class="text-body-2">{{ entry.avgResults != null ? entry.avgResults : '—' }}</td>
                <td>
                  <v-chip v-if="entry.avgSpeed != null && entry.avgSpeed > 0" x-small :color="speedColor(entry.avgSpeed)" text-color="white">
                    {{ entry.avgSpeed }}ms
                  </v-chip>
                  <span v-else class="text-body-2">—</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <v-row class="mt-4">
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 mekko-card">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                By Seniority
              </h3>
              <div v-if="seniorityRows.length > 0">
                <div class="mekko-bar mb-3">
                  <div
                    v-for="(row, i) in seniorityRows"
                    :key="row.label"
                    class="mekko-segment"
                    :style="{ width: row.pct + '%', backgroundColor: mekkoColor(i) }"
                    :title="row.label + ': ' + row.count + ' (' + row.pct + '%)'"
                  ></div>
                </div>
                <div class="mekko-table-wrap">
                  <table class="mekko-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th class="text-left">Level</th>
                        <th class="text-right">Count</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in seniorityRows" :key="row.label">
                        <td><span class="mekko-dot" :style="{ backgroundColor: mekkoColor(i) }"></span></td>
                        <td class="text-body-2">{{ row.label }}</td>
                        <td class="text-body-2 text-right font-weight-bold">{{ row.count }}</td>
                        <td class="text-body-2 text-right">{{ row.pct }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No data
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 mekko-card">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                By Cluster
              </h3>
              <div v-if="clusterRows.length > 0">
                <div class="mekko-bar mb-3">
                  <div
                    v-for="(row, i) in clusterRows"
                    :key="row.label"
                    class="mekko-segment"
                    :style="{ width: row.pct + '%', backgroundColor: mekkoColor(i) }"
                    :title="row.label + ': ' + row.count + ' (' + row.pct + '%)'"
                  ></div>
                </div>
                <div class="mekko-table-wrap">
                  <table class="mekko-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th class="text-left">Cluster</th>
                        <th class="text-right">Count</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in clusterRows" :key="row.label">
                        <td><span class="mekko-dot" :style="{ backgroundColor: mekkoColor(i) }"></span></td>
                        <td class="text-body-2">{{ row.label }}</td>
                        <td class="text-body-2 text-right font-weight-bold">{{ row.count }}</td>
                        <td class="text-body-2 text-right">{{ row.pct }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No data
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 mekko-card">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                By Functional Area
              </h3>
              <div v-if="functionalAreaRows.length > 0">
                <div class="mekko-bar mb-3">
                  <div
                    v-for="(row, i) in functionalAreaRows"
                    :key="row.label"
                    class="mekko-segment"
                    :style="{ width: row.pct + '%', backgroundColor: mekkoColor(i) }"
                    :title="row.label + ': ' + row.count + ' (' + row.pct + '%)'"
                  ></div>
                </div>
                <div class="mekko-table-wrap">
                  <table class="mekko-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th class="text-left">Area</th>
                        <th class="text-right">Count</th>
                        <th class="text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in functionalAreaRows" :key="row.label">
                        <td><span class="mekko-dot" :style="{ backgroundColor: mekkoColor(i) }"></span></td>
                        <td class="text-body-2">{{ row.label }}</td>
                        <td class="text-body-2 text-right font-weight-bold">{{ row.count }}</td>
                        <td class="text-body-2 text-right">{{ row.pct }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-4 grey--text">
                No data
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <AuditPanel
          :audits="safeAuditLog"
          :page="auditPage"
          :page-size="auditPageSize"
          :total-entries="auditTotalEntries"
          :total-pages="auditTotalPages"
          :is-super-admin="isSuperAdmin"
          @page-change="onAuditPageChange"
          @page-size-change="onAuditPageSizeChange"
        />
      </v-tab-item>
    </v-tabs-items>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import AuditPanel from "../../components/Intelligence/AuditPanel.vue";

export default {
  name: "IntelligenceAnalytics",
  components: { AuditPanel },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedPeriodIdx: 2,
      periodOptions: [7, 14, 30],
      searchesChartInstance: null,
      healthMetricsOpen: false,
    };
  },
  computed: {
    ...mapState("intelligenceAnalytics", [
      "auditLog",
      "auditPage",
      "auditPageSize",
      "auditTotalEntries",
      "auditTotalPages",
      "isLoading",
      "hasError",
      "errorMessage",
    ]),
    ...mapGetters("intelligenceAnalytics", [
      "searchesOverTime",
      "profileInventory",
      "bySeniority",
      "byFunctionalArea",
      "bySpecialistCluster",
      "hasData",
    ]),
    activeTab: {
      get() {
        return this.$store.state.intelligenceAnalytics.activeTab;
      },
      set(val) {
        this.$store.commit("intelligenceAnalytics/updateField", { path: "activeTab", value: val });
      },
    },
    isSuperAdmin() {
      return !!(this.$store.state.auth && this.$store.state.auth.companySuperAdmin);
    },
    safeAuditLog() {
      const val = this.$store.state.intelligenceAnalytics
        ? this.$store.state.intelligenceAnalytics.auditLog
        : [];
      return Array.isArray(val) ? val : [];
    },
    inventoryStats() {
      if (!this.profileInventory) return [];
      return [
        { label: "Total Profiles", value: this.profileInventory.totalProfiles || 0, color: this.$vuetify.theme.dark ? "#90CAF9" : "#1E3A5F" },
        { label: "Total Achievements", value: this.profileInventory.totalAchievements || 0, color: "#007AFF" },
        { label: "Total Skills", value: this.profileInventory.totalSkills || 0, color: "#8B5CF6" },
        { label: "Avg Score", value: this.profileInventory.avgScore || '—', color: "#F59E0B" },
      ];
    },
    staticMetrics() {
      if (!this.profileInventory) return [];
      const inv = this.profileInventory;
      const totalProfiles = inv.totalProfiles || 1;
      const totalAchievements = inv.totalAchievements || 0;
      const totalSkills = inv.totalSkills || 0;
      return [
        { label: "Achievements / Profile", value: totalProfiles > 0 ? (totalAchievements / totalProfiles).toFixed(1) : '0' },
        { label: "Unique Skills / Profile", value: totalProfiles > 0 ? (totalSkills / totalProfiles).toFixed(1) : '0' },
        { label: "STAR Completion Rate", value: inv.avgStarCompleteness != null ? Math.round(inv.avgStarCompleteness) + '%' : '—' },
        { label: "Profiles w/ Credentials", value: (inv.profilesWithCredentials || 0) + '%' },
        { label: "Avg STAR Elements", value: inv.avgStarElements != null ? inv.avgStarElements.toFixed(1) + '/4' : '—' },
      ];
    },
    periodMetrics() {
      if (!this.profileInventory) return [];
      const inv = this.profileInventory;
      const searches = this.searchesOverTime.reduce((s, e) => s + (e.count || 0), 0);
      const totalProfiles = inv.totalProfiles || 1;
      return [
        { label: "Searches This Period", value: searches },
        { label: "Searches / Profile", value: totalProfiles > 0 ? (searches / totalProfiles).toFixed(2) : '0' },
        { label: "New Achievements", value: inv.newAchievements || 0 },
        { label: "Active Profiles", value: inv.activeProfiles || '—' },
      ];
    },
    seniorityRows() {
      return this.toMekkoRows(this.bySeniority);
    },
    clusterRows() {
      return this.toMekkoRows(this.bySpecialistCluster);
    },
    functionalAreaRows() {
      return this.toMekkoRows(this.byFunctionalArea);
    },
  },
  watch: {
    selectedPeriodIdx(newIdx) {
      const days = this.periodOptions[newIdx];
      this.loadAnalytics(days);
      this.loadAuditLog(days);
    },
    searchesOverTime() {
      this.$nextTick(() => this.renderSearchesChart());
    },
  },
  mounted() {
    const days = this.periodOptions[this.selectedPeriodIdx];
    this.loadAnalytics(days);
    this.loadAuditLog(days);
    if (this.$route && this.$route.query && this.$route.query.subtab === "audit") {
      this.$store.commit("intelligenceAnalytics/updateField", { path: "activeTab", value: 1 });
    }
  },
  beforeDestroy() {
    if (this.searchesChartInstance) this.searchesChartInstance.destroy();
  },
  methods: {
    ...mapActions("intelligenceAnalytics", ["loadAnalytics", "loadAuditLog", "changeAuditPage"]),
    onAuditPageChange(page) {
      this.changeAuditPage(page);
    },
    onAuditPageSizeChange(size) {
      this.$store.commit("intelligenceAnalytics/updateField", { path: "auditPageSize", value: size });
      this.loadAuditLog({ periodDays: this.periodOptions[this.selectedPeriodIdx], page: 1, pageSize: size });
    },
    speedColor(ms) {
      if (!ms) return "#9E9E9E";
      if (ms <= 200) return "#10B981";
      if (ms <= 500) return "#F59E0B";
      return "#EF4444";
    },
    isDark() {
      return this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark;
    },
    chartTextColor() {
      return this.isDark() ? '#ccc' : '#666';
    },
    chartGridColor() {
      return this.isDark() ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
    },
    chartColors() {
      return [
        '#007AFF', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
        '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
        '#84CC16', '#A855F7', '#0EA5E9', '#D946EF', '#22C55E',
      ];
    },
    loadChartJs() {
      return new Promise((resolve, reject) => {
        if (typeof window.Chart !== 'undefined') {
          resolve(window.Chart);
          return;
        }
        const existing = document.querySelector('script[src*="chart.js"]') || document.querySelector('script[src*="Chart"]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.Chart));
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
        script.onload = () => resolve(window.Chart);
        script.onerror = () => reject(new Error('Failed to load Chart.js'));
        document.head.appendChild(script);
      });
    },
    async renderSearchesChart() {
      if (!this.$refs.searchesChart || !this.searchesOverTime.length) return;

      try {
        await this.loadChartJs();
      } catch (e) {
        return;
      }

      if (this.searchesChartInstance) {
        this.searchesChartInstance.destroy();
        this.searchesChartInstance = null;
      }

      const labels = this.searchesOverTime.map(e => e.date.substring(5));
      const data = this.searchesOverTime.map(e => e.count);
      const dark = this.isDark();

      const ctx = this.$refs.searchesChart.getContext('2d');
      this.searchesChartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Searches',
            data,
            backgroundColor: dark ? 'rgba(0, 122, 255, 0.7)' : 'rgba(0, 122, 255, 0.6)',
            borderColor: '#007AFF',
            borderWidth: 1,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
          },
          scales: {
            x: {
              ticks: { color: this.chartTextColor(), maxRotation: 45 },
              grid: { color: this.chartGridColor() },
            },
            y: {
              beginAtZero: true,
              ticks: { color: this.chartTextColor(), precision: 0 },
              grid: { color: this.chartGridColor() },
            },
          },
        },
      });
    },
    toMekkoRows(dataObj) {
      if (!dataObj || typeof dataObj !== 'object') return [];
      let entries = [];
      if (Array.isArray(dataObj)) {
        entries = dataObj.map(item => {
          const label = item.level || item.cluster || item.function || item.area || item.label || item.name || 'Unknown';
          const count = item.count || item.value || 0;
          return [label, count];
        });
      } else {
        entries = Object.entries(dataObj);
      }
      entries.sort((a, b) => b[1] - a[1]);
      const total = entries.reduce((s, e) => s + (typeof e[1] === 'number' ? e[1] : 0), 0);
      if (total === 0) return [];
      return entries.filter(([, c]) => typeof c === 'number' && c > 0).map(([label, count]) => ({
        label: String(label) || 'Unknown',
        count,
        pct: Math.round((count / total) * 100) || 1,
      }));
    },
    mekkoColor(index) {
      const colors = this.chartColors();
      return colors[index % colors.length];
    },
  },
};
</script>

<style scoped>
.intelligence-analytics {
  max-width: 1400px;
}
.chart-container {
  position: relative;
  width: 100%;
}
.mekko-card {
  display: flex;
  flex-direction: column;
}
.mekko-bar {
  display: flex;
  height: 28px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.06);
}
.mekko-segment {
  min-width: 4px;
  transition: opacity 0.2s;
}
.mekko-segment:hover {
  opacity: 0.75;
}
.mekko-table-wrap {
  max-height: 220px;
  overflow-y: auto;
}
.mekko-table {
  width: 100%;
  border-collapse: collapse;
}
.mekko-table th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 4px;
  border-bottom: 1px solid rgba(128,128,128,0.2);
}
.mekko-table td {
  padding: 5px 4px;
  border-bottom: 1px solid rgba(128,128,128,0.08);
}
.mekko-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.theme--dark .mekko-bar {
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.1);
}
.theme--dark .mekko-table th {
  border-bottom-color: rgba(255,255,255,0.15);
}
.theme--dark .mekko-table td {
  border-bottom-color: rgba(255,255,255,0.06);
}
.health-metric-card {
  border: 1px solid var(--resu-border, #e0e0e0);
  border-radius: 8px;
}
.theme--dark .health-metric-card {
  border-color: rgba(255,255,255,0.12);
}
</style>
